import React, { useState } from 'react';
import { ArrowRight, Check, RotateCw } from 'lucide-react';
import './PersonalityTest.css';

const PersonalityTest = () => {
  // State management
  const [step, setStep] = useState('intro'); // 'intro', 'questions', 'submit', 'processing', 'results'
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [isProcessingQ28, setIsProcessingQ28] = useState(false); // Track when Q28 answer is being processed
  
  // Handle trick question 28
  const [q28Options, setQ28Options] = useState([
    "Luca",
    "Christina",
    "Joseph Smith",
    "Pan Dong"
  ]);

  // Questions for the personality test based on the 8 personality types
  const questions = [
    {
      id: 0,
      text: "Utah Personality Test",
      description: "This AI-powered personality test reveals insights about your ideal partner based on your unique preferences. Built on behavioral research tailored specifically to Utah's cultural and social landscape, the test asks 40 carefully crafted questions. Answer honestly to unlock a personalized profile of your perfect match.",
      isIntro: true
    },
    {
      id: 1,
      text: "How do you feel about planning for dates?",
      options: [
        "I love planning every detail meticulously",
        "I prefer a balanced approach with some structure",
        "I'm spontaneous and prefer minimal planning",
        "I'd rather the other person handle the planning"
      ]
    },
    {
      id: 2,
      text: "How important is physical fitness to you in a relationship?",
      options: [
        "Extremely - I want a partner with fitness goals",
        "Somewhat - I appreciate an active lifestyle",
        "Not very - I don't mind if they're not fit",
        "Not at all - I prefer other qualities"
      ]
    },
    {
      id: 3,
      text: "How do you feel about ball sports (pickleball, golf, table tennis, etc.)?",
      options: [
        "I'm obsessed with them - they're a major part of my life",
        "I enjoy them occasionally",
        "I'm indifferent to ball sports",
        "I actively dislike ball sports"
      ]
    },
    {
      id: 4,
      text: "What's your approach to punctuality?",
      options: [
        "I'm always punctual except for specific social events",
        "I'm always on time no matter what",
        "I'm occasionally late but try my best",
        "I'm chronically late for most things"
      ]
    },
    {
      id: 5,
      text: "How do you feel about partners with unique or niche hobbies?",
      options: [
        "Love it - passion for unusual interests is great",
        "Prefer common, relatable hobbies",
        "Indifferent if we share some interests",
        "Prefer a partner with the same hobbies as me"
      ]
    },
    {
      id: 6,
      text: "What age range would you prefer in a partner?",
      options: [
        "Age doesn't matter to me",
        "Close to my age (within 5 years)",
        "Much younger than me is preferable",
        "A little older than me is preferable"
      ]
    },
    {
      id: 7,
      text: "What is an acceptable amount of your income to tithe?",
      options: [
        "100%",
        "80%",
        "10%",
        "0%"
      ]
    },
    {
      id: 8,
      text: "How do you feel about cooking skills in a partner?",
      options: [
        "Very important - I want someone who cooks well",
        "Somewhat important - basic cooking skills are nice",
        "Not important - I don't mind if they can't cook",
        "I prefer to do all the cooking myself"
      ]
    },
    {
      id: 9,
      text: "Would you want your partner to join your activities even if they're not interested?",
      options: [
        "Yes - I value their presence regardless",
        "Sometimes - for important events only",
        "Rarely - only what they truly enjoy",
        "Never - we should have separate activities"
      ]
    },
    {
      id: 10,
      text: "How do you feel about making the first move in a relationship?",
      options: [
        "I'm very hesitant to make the first move",
        "I can make the first move but prefer not to",
        "I'm comfortable making the first move",
        "I always make the first move"
      ]
    },
    {
      id: 11,
      text: "How important are financial goals in your relationship?",
      options: [
        "Very important - we need to align completely on finances",
        "Important - but there's room for differences",
        "Somewhat important - as long as they're not wasteful",
        "Not particularly important to me"
      ]
    },
    {
      id: 12,
      text: "How do you prioritize finding a partner versus other life goals?",
      options: [
        "Finding a partner is secondary to my other pursuits",
        "It's equally important as my other goals",
        "It's one of my top priorities",
        "It's my main focus right now"
      ]
    },
    {
      id: 13,
      text: "What's an ideal location for your first date?",
      options: [
        "Discord",
        "McDonald's",
        "Snowbird",
        "Momentum Fort Union"
      ]
    },
    {
      id: 14,
      text: "How important is it for your partner to get along with your friends?",
      options: [
        "Yes - they should fit with my friends easily",
        "Somewhat - some similar traits would be nice",
        "Not necessarily - they should be unique",
        "Definitely not - I want someone different"
      ]
    },
    {
      id: 15,
      text: "How interested are you in other cultures and languages?",
      options: [
        "Extremely - fascinated by cultural differences",
        "Somewhat - I appreciate cultural diversity",
        "Not particularly - prefer familiar cultures",
        "Not at all - I focus on my own culture"
      ]
    },
    {
      id: 16,
      text: "How do you feel about dating someone with a different cultural background?",
      options: [
        "I seek someone from a different culture",
        "I'm open to it and find it appealing",
        "I'm neutral about cultural background",
        "I prefer dating within my own culture"
      ]
    },
    {
      id: 17,
      text: "How important is intellectual compatibility in your relationships?",
      options: [
        "Essential - we must be on the same wavelength",
        "Important - but interests can differ",
        "Somewhat - other factors matter more",
        "Not very important to me"
      ]
    },
    {
      id: 18,
      text: "How do you feel about a partner who consistently beats you at board games?",
      options: [
        "I love the challenge and find it attractive",
        "I enjoy it occasionally but not all the time",
        "I prefer to win at least sometimes",
        "I dislike losing and would find it frustrating"
      ]
    },
    {
      id: 19,
      text: "How perfectionist are you?",
      options: [
        "Extremely - I notice and care about every detail",
        "Quite perfectionist in specific areas",
        "Somewhat - I appreciate quality but can let things go",
        "Not particularly - I focus on the big picture"
      ]
    },
    {
      id: 20,
      text: "How important is musical talent in a partner?",
      options: [
        "Very important - I'm attracted to musical ability",
        "Somewhat important - it's a nice bonus",
        "Not important - though it's a pleasant surprise",
        "Not important at all to me"
      ]
    },
    {
      id: 21,
      text: "How do you feel about climbing as an activity?",
      options: [
        "I love climbing and do it regularly",
        "I only go when I can borrow Randy's guest pass",
        "I've never tried it but might be interested",
        "I have no interest in climbing"
      ]
    },
    {
      id: 22,
      text: "How would you describe your expectations for relationships?",
      options: [
        "Very specific and potentially hard to meet",
        "Reasonable but I know what I want",
        "Flexible and open to different types of people",
        "Minimal - I don't have many specific requirements"
      ]
    },
    {
      id: 23,
      text: "How do you feel about motorcycles?",
      options: [
        "I find them and people who ride them very attractive",
        "They're cool but not a big deal to me",
        "I'm neutral about motorcycles",
        "I find them unappealing or concerning"
      ]
    },
    {
      id: 24,
      text: "What would be your dream proposal?",
      options: [
        "After recreating our first date, sharing ice cream",
        "Temple Square stroll, quiet proposal near lit trees",
        "Sunset hike to Kesler Peak, ring reveal at the summit",
        "Before BBQ with friends, while shopping at Smith's"
      ]
    },
    {
      id: 25,
      text: "How important is it that your partner enjoys quality coffee?",
      options: [
        "Very important - I'm a coffee enthusiast",
        "Somewhat important - I appreciate good coffee",
        "Not important - though it's a nice bonus",
        "Not important at all - I don't care about coffee"
      ]
    },
    {
      id: 26,
      text: "How do you feel about a partner who is dedicated to self-improvement?",
      options: [
        "Strongly prefer it - constant growth is essential",
        "Like it - but not if it becomes obsessive",
        "Neutral - it's fine either way",
        "Prefer someone more content and settled"
      ]
    },
    {
      id: 27,
      text: "Which personality trait do you think earns the most respect?",
      options: [
        "Humility",
        "Modesty",
        "Big penis",
        "Unpretentiousness"
      ],
      isDummy: true
    },
    {
      id: 28,
      text: "Who do you consider the most worthy of respect?",
      options: [
        "Luca",
        "Christina",
        "Joseph Smith",
        "Pan Dong"
      ],
      isTrick: true
    },
    {
      id: 29,
      text: "How important is loyalty and commitment in a relationship?",
      options: [
        "Essential - I need someone who never leaves",
        "Very important with healthy boundaries",
        "Important but I value independence too",
        "I prefer keeping things casual"
      ]
    },
    {
      id: 30,
      text: "How do you feel about a partner who is career-focused?",
      options: [
        "Prefer someone with strong career ambitions",
        "Like a balance of career and personal life",
        "Don't mind either way",
        "Prefer focus on personal life over career"
      ]
    },
    {
      id: 31,
      text: "How important is it that your partner shares your ideological beliefs?",
      options: [
        "Essential - we must align on most issues",
        "Important - alignment on key issues is enough",
        "Not very - I enjoy different perspectives",
        "Not at all - I prefer diversity of thought"
      ]
    },
    {
      id: 32,
      text: "How do you feel about watching sports with a partner?",
      options: [
        "Love it - especially unique or niche sports",
        "Enjoy mainstream sports only",
        "Occasionally enjoyable but not regularly",
        "Not interested in watching sports at all"
      ]
    },
    {
      id: 33,
      text: "How important is physical appearance in your choice of partner?",
      options: [
        "Very important - physical attraction is essential",
        "Important but personality matters more",
        "Somewhat important but can grow with connection",
        "Not particularly important compared to other qualities"
      ]
    },
    {
      id: 34,
      text: "Would you date someone who doesn't share your food preferences?",
      options: [
        "Probably not - food compatibility is important to me",
        "It depends on how different their preferences are",
        "Yes - food preferences aren't that important",
        "Absolutely - I enjoy culinary diversity"
      ]
    },
    {
      id: 35,
      text: "How do you feel about a partner with a side business or project?",
      options: [
        "Love it - I'm attracted to entrepreneurial spirit",
        "It's fine as long as it doesn't take over their life",
        "Neutral - doesn't matter much to me",
        "Prefer someone focused on one main career"
      ]
    },
    {
      id: 36,
      text: "How do you feel about collecting hobbies (watches, stamps, etc.)?",
      options: [
        "I love collecting and appreciate it in a partner",
        "I don't collect but find it an interesting trait",
        "Neutral - doesn't affect my attraction",
        "I find collecting hobbies unappealing"
      ]
    },
    {
      id: 37,
      text: "How important is it that your partner is optimistic?",
      options: [
        "Very important - I need a positive partner",
        "Somewhat important - balanced optimism is nice",
        "Not very important - realism is more valuable",
        "Not important - I appreciate various outlooks"
      ]
    },
    {
      id: 38,
      text: "How do you feel about university education?",
      options: [
        "Very important - I value higher education highly",
        "Somewhat important but not essential",
        "Not important - other forms of intelligence matter more",
        "Not important at all in my choice of partner"
      ]
    },
    {
      id: 39,
      text: "How do you feel about a partner who works remotely?",
      options: [
        "I prefer it - I like the flexibility it provides",
        "I don't mind either way",
        "I slightly prefer someone who works in-person",
        "I strongly prefer someone who works outside the home"
      ]
    },
    {
      id: 40,
      text: "What's your ideal first date?",
      options: [
        "Something active and outdoorsy",
        "A quiet, intimate dinner",
        "Something fun and creative",
        "Something casual like coffee or drinks"
      ]
    }
  ];

  // Personality results with descriptions closer to the original
  const results = [
    {
      id: 1,
      location: "The Shy Subaru Boy",
      description: "Your ideal partner would be a QA engineer who works remotely and runs a cafe as a side gig. They're never punctual except for church. They make a good nitro-infused cold brew, can climb V7, and tend to overplan first dates. Most importantly, they'll never abandon you and will set ambitious fitness goals to help you grow. Their loyalty and diverse talents make them a dependable partner when you need them most."
    },
    {
      id: 2,
      location: "Alexander Qi Jianran",
      description: "You have refreshingly straightforward expectations for relationships. Your ideal partner is obsessed with ball sports—pickleball, tennis, ping pong, really any kind of ball. Their entire life is dedicated to these activities, which you find endearing rather than one-dimensional. They likely attend the University of Utah and bring an uncomplicated enthusiasm to your relationship that's a welcome change from complexity."
    },
    {
      id: 3,
      location: "The Motorcycle Guy",
      description: "You have somewhat unrealistic expectations in relationships. Your ideal partner exudes coolness and confidence, likely rides a motorcycle, and has a distinctive style. They have specific preferences, like not enjoying overly-seasoned shrimp. They tend to favor youth and vigor, and might not be interested in dating anyone over 30. Your standards are specific, but when you find the right match, the connection is undeniable."
    },
    {
      id: 4,
      location: "The NoVA Transplant",
      description: "It's essential that your partner shares your ideological beliefs and worldview. Your ideal match excels at cooking but might not share your interest in physical activities like climbing. You're drawn to someone who is attached, romantic, idealistic, and maintains a positive outlook. You appreciate how they willingly join you at the gym or social gatherings even when they have nothing to do there—their presence and support matter more than shared interests."
    },
    {
      id: 5,
      location: "The Snowbird Powder Pixie",
      description: "You're hesitant to make the first romantic move and may have even taken a pledge to free yourself from worldly pleasures for 4 years. Financial compatibility is crucial—you want a partner who aligns with your goals and isn't excessive with spending. Health optimization is important to you both. While you value relationships, other pursuits take priority: skiing, collecting watches, watching sumo matches, and constant self-improvement. The right person will understand this balance."
    },
    {
      id: 6,
      location: "The Average Hinge Date",
      description: "You find making the first move in person challenging. Online connections feel more comfortable and natural. Your ideal partner has two key qualities: they're physically attractive to you, while possessing a personality that's remarkably similar to the average of all your close friends. This combination creates someone who fits easily into your existing social circle while providing the spark of attraction you desire."
    },
    {
      id: 7,
      location: "The Round One Lurker",
      description: "You're intellectually curious and have a playful streak shaped by elements of weeb culture. You value deep conversations and thrive when connecting with someone on the same wavelength—especially when there's a friendly challenge involved, like a strategic board game. You're drawn to the idea of dating someone from a different cultural background, finding excitement in the exchange of perspectives. Rhythm games like Music Diver captivate you."
    },
    {
      id: 8,
      location: "The Perfect Stepsister",
      description: "You were dropped as a baby, resulting in a uniquely imaginative perspective. As a perfectionist with OCD tendencies, you notice and overthink every detail. Coincidentally, you've found that your stepsister (who's not related by blood) meets all your rigorous standards. They possess musical talent with an excellent sense of rhythm, impressive intelligence, and somehow their hair looks perfect even first thing in the morning. Your careful observation has revealed the ideal match was close by all along."
    }
  ];

  // Handle continuing from intro to questions
  const handleContinue = () => {
    setStep('questions');
  };

  // Handle selecting an answer
  const handleAnswer = (questionId, answerIndex) => {
    // Update answers
    setAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
    
    // Special handling for trick question 28
    if (questionId === 28) {
      // Check if they selected "Answer 1" or if their selection is "Answer 1"
      const selectedOption = q28Options[answerIndex];
      if (answerIndex !== 0 && selectedOption !== "Luca") {
        // Set processing state to true to prevent showing the "Next" button
        setIsProcessingQ28(true);
        
        // They chose a wrong answer - modify the options
        const newOptions = [...q28Options];
        newOptions[answerIndex] = newOptions[0]; // Replace chosen option with "Answer 1"
        setQ28Options(newOptions);
        
        // Remove their answer so they can answer again
        setTimeout(() => {
          setAnswers(prev => {
            const newAnswers = { ...prev };
            delete newAnswers[28];
            return newAnswers;
          });
          
          // Reset processing state after answer is cleared
          setIsProcessingQ28(false);
        }, 300);
        
        return; // Don't continue to next question
      }
      // If they picked "Answer 1" (original or duplicated), continue to next question
    }
    
    // Auto-advance to next question after first time answering
    if (questionId < 40) {
      setCurrentQuestion(questionId + 1);
    }
  };

  // Go to previous question
  const goToPreviousQuestion = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  // Go to next question
  const goToNextQuestion = () => {
    if (currentQuestion < 40) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setStep('submit');
    }
  };

  // Handle submit button click
  const handleSubmit = () => {
    setStep('processing');
    
    // Process results after a delay
    setTimeout(() => {
      calculateResult();
      setStep('results');
    }, 2000);
  };

  // Calculate result based on answers with more sophisticated scoring
  const calculateResult = () => {
    // Initialize scores for each personality type
    const scores = {
      1: 0, // Church
      2: 0, // Pickleball Court
      3: 0, // Chinese TikTok
      4: 0, // Virginia
      5: 0, // Ski Lift
      6: 0, // Dating App
      7: 0, // Japan
      8: 0  // At Home
    };

    // Score each answer (excluding dummy questions)
    for (const [questionId, answerIndex] of Object.entries(answers)) {
      const qId = parseInt(questionId);
      
      // Skip dummy questions
      if (qId === 27 || qId === 28) continue;
      
      // More sophisticated scoring system that allows multiple answers to contribute to different result types
      switch(qId) {
        // Planning for dates
        case 1:
          if (answerIndex === 0) scores[1] += 2.0; // Church: meticulous planning
          if (answerIndex === 0) scores[8] += 1.5; // At Home: perfectionist
          if (answerIndex === 0 || answerIndex === 1) scores[4] += 0.7; // Virginia: structured
          if (answerIndex === 2) scores[3] += 0.8; // Chinese TikTok: spontaneous
          if (answerIndex === 3) scores[2] += 0.5; // Pickleball: casual
          break;
          
        // Physical fitness importance
        case 2:
          if (answerIndex === 0) scores[1] += 2.0; // Church: sets fitness goals
          if (answerIndex === 0 || answerIndex === 1) scores[5] += 1.0; // Ski Lift: health optimization
          if (answerIndex === 2 || answerIndex === 3) scores[4] += 0.7; // Virginia: not into fitness
          break;
          
        // Ball sports
        case 3:
          if (answerIndex === 0) scores[2] += 2.5; // Pickleball: obsessed with ball sports
          if (answerIndex === 1) scores[2] += 0.8; // Pickleball: enjoys occasionally
          if (answerIndex === 3) scores[7] += 0.5; // Japan: nerd, different interests
          break;
          
        // Punctuality
        case 4:
          if (answerIndex === 0) scores[1] -= 1.8; // Church: never punctual except for church
          if (answerIndex === 1) scores[8] -= 3.0; // At Home: perfectionist
          if (answerIndex === 3) scores[3] -= 0.7; // Chinese TikTok: less conventional
          break;
          
        // Niche hobbies
        case 5:
          if (answerIndex === 0) scores[1] += 0.6; // Church: diverse skills
          if (answerIndex === 0) scores[5] += 0.8; // Ski Lift: niche interests
          if (answerIndex === 0) scores[7] += 0.7; // Japan: weeb interests
          if (answerIndex === 3) scores[4] += 0.8; // Virginia: wants shared interests
          break;
          
        // Age preferences
        case 6:
          if (answerIndex === 2) scores[3] += 2.0; // Chinese TikTok: prefer younger, not over 30
          if (answerIndex === 2) scores[8] += 4.0; // At Home: prefer younger
          if (answerIndex === 0) scores[7] += 0.6; // Japan: open to differences
          break;
          
        // Shared ideological beliefs
        case 7:
          if (answerIndex === 0 || answerIndex === 1) scores[8] += 2.0; // Home: similar perspectives
          if (answerIndex === 2 || answerIndex === 3) scores[7] += 0.1; // Japan: different perspectives
          break;
          
        // Cooking skills
        case 8:
          if (answerIndex === 0) scores[4] += 1.8; // Virginia: good at cooking
          if (answerIndex === 3) scores[1] += 0.7; // Church: other skills matter more
          break;
          
        // Partner joining activities
        case 9:
          if (answerIndex === 0) scores[4] += 2.0; // Virginia: follows to activities
          if (answerIndex === 3) scores[5] += 0.8; // Ski Lift: independent activities
          break;
          
        // Making first move
        case 10:
          if (answerIndex === 0) scores[5] += 1.8; // Ski Lift: hesitant to make move
          if (answerIndex === 0) scores[6] += 1.5; // Dating App: not good at first move
          if (answerIndex === 2 || answerIndex === 3) scores[3] += 0.7; // Chinese TikTok: confidence
          break;
          
        // Financial goals
        case 11:
          if (answerIndex === 0) scores[5] += 2.0; // Ski Lift: financial alignment
          if (answerIndex === 3) scores[3] += 0.6; // Chinese TikTok: less conventional
          break;
          
        // Priorities beyond relationships
        case 12:
          if (answerIndex === 0) scores[5] += 2.0; // Ski Lift: other pursuits priority
          if (answerIndex === 3) scores[6] += 0.8; // Dating App: focused on relationships
          break;
          
        // Online vs in-person approach
        case 13:
          if (answerIndex === 0) scores[6] += 2.0; // Dating App: more comfortable online
          if (answerIndex === 2) scores[2] += 0.6; // Pickleball: in-person activities
          break;
          
        // Partner similar to friends
        case 14:
          if (answerIndex === 0) scores[6] += 2.0; // Dating App: personality like friends
          if (answerIndex === 3) scores[7] += 0.8; // Japan: different background
          break;
          
        // Interest in cultures
        case 15:
          if (answerIndex === 0) scores[7] += 2.0; // Japan: fascinated by cultural differences
          if (answerIndex === 3) scores[2] += 0.6; // Pickleball: simple, local interests
          break;
          
        // Different cultural background
        case 16:
          if (answerIndex === 0) scores[7] += 2.0; // Japan: seeks different culture
          if (answerIndex === 3) scores[4] += 0.8; // Virginia: shared background
          break;
          
        // Intellectual compatibility
        case 17:
          if (answerIndex === 0) scores[7] += 1.8; // Japan: same wavelength
          if (answerIndex === 0) scores[8] += 1.0; // At Home: intelligent
          if (answerIndex === 3) scores[2] += 1.0; // Pickleball: simpler connection
          break;
          
        // Partner beats at games
        case 18:
          if (answerIndex === 0) scores[7] += 1.8; // Japan: beats at board games
          if (answerIndex === 3) scores[3] += 0.6; // Chinese TikTok: competitive
          break;
          
        // Perfectionism
        case 19:
          if (answerIndex === 0) scores[8] += 2.5; // At Home: perfectionist with OCD
          if (answerIndex === 0) scores[1] += 0.8; // Church: meticulous planning
          if (answerIndex === 3) scores[2] += 0.7; // Pickleball: relaxed approach
          break;
          
        // Musical talent
        case 20:
          if (answerIndex === 0) scores[8] += 2.0; // At Home: musical ability
          break;
          
        // Climbing
        case 21:
          if (answerIndex === 0) scores[1] += 1.8; // Church: climbs V7
          if (answerIndex === 3) scores[4] += 1.5; // Virginia: bad at climbing
          break;
          
        // Relationship expectations
        case 22:
          if (answerIndex === 0) scores[3] += 1.8; // Chinese TikTok: unrealistic expectations
          if (answerIndex === 0) scores[8] += 1.0; // At Home: exacting standards
          if (answerIndex === 3) scores[2] += 2.0; // Pickleball: low expectations
          break;
          
        // Motorcycles
        case 23:
          if (answerIndex === 0) scores[3] += 2.5; // Chinese TikTok: motorcycle
          break;
          
        // Romantic/idealistic
        case 24:
          if (answerIndex === 0) scores[4] += 1.8; // Virginia: romantic, idealistic
          if (answerIndex === 3) scores[5] += 0.8; // Ski Lift: practical
          break;
          
        // Coffee
        case 25:
          if (answerIndex === 0) scores[1] += 1.8; // Church: nitro cold brew
          break;
          
        // Self-improvement
        case 26:
          if (answerIndex === 0) scores[5] += 1.8; // Ski Lift: self-improvement
          if (answerIndex === 0) scores[1] += 0.7; // Church: fitness goals
          if (answerIndex === 3) scores[4] += 0.6; // Virginia: accepts as is
          break;
          
        // Loyalty/commitment
        case 29:
          if (answerIndex === 0) scores[1] += 2.0; // Church: never abandons
          if (answerIndex === 3) scores[3] += 0.8; // Chinese TikTok: casual
          break;
          
        // Career focus
        case 30:
          if (answerIndex === 0) scores[1] += 0.7; // Church: QA engineer
          if (answerIndex === 0) scores[5] += 0.7; // Ski Lift: ambitions
          break;
          
        // Spirituality/religious
        case 31:
          if (answerIndex === 0) scores[1] += 1.5; // Church: religious context
          if (answerIndex === 0 || answerIndex === 1) scores[8] += 2.0; // Home: similar perspectives
          if (answerIndex === 0) scores[4] += 0.8; // Virginia: shared values
          break;
          
        // Watching sports
        case 32:
          if (answerIndex === 0) scores[5] += 1.5; // Ski Lift: sumo matches
          if (answerIndex === 0 || answerIndex === 1) scores[2] += 1.5; // Pickleball: ball sports
          break;
          
        // Physical appearance
        case 33:
          if (answerIndex === 0) scores[6] += 1.8; // Dating App: attractive
          if (answerIndex === 0) scores[8] += 1.7; // At Home: hair looks good
          if (answerIndex === 0) scores[3] += 0.8; // Chinese TikTok: appearance
          break;
          
        // Food preferences
        case 34:
          if (answerIndex === 0) scores[3] += 1.5; // Chinese TikTok: food preferences (shrimp)
          break;
          
        // Side business
        case 35:
          if (answerIndex === 0) scores[1] += 2.0; // Church: runs cafe as side gig
          break;
          
        // Collecting hobbies
        case 36:
          if (answerIndex === 0) scores[5] += 1.8; // Ski Lift: collecting watches
          break;
          
        // Optimism
        case 37:
          if (answerIndex === 0) scores[4] += 1.5; // Virginia: positive
          break;
          
        // University education
        case 38:
          if (answerIndex === 0) scores[2] += 2.0; // Pickleball: University of Utah
          if (answerIndex === 0) scores[8] += 0.8; // At Home: intelligence
          break;
          
        // Remote work
        case 39:
          if (answerIndex === 0) scores[1] += 1.8; // Church: works remotely
          break;
          
        // First date
        case 40:
          if (answerIndex === 0) scores[1] += 0.8; // Church: active
          if (answerIndex === 0) scores[5] += 0.8; // Ski Lift: outdoorsy
          if (answerIndex === 1) scores[4] += 0.8; // Virginia: intimate
          if (answerIndex === 2) scores[7] += 0.8; // Japan: creative
          if (answerIndex === 3) scores[6] += 0.8; // Dating App: casual
          break;
      }
    }
    
    // Apply base scores to ensure results are balanced when few questions are answered
    scores[1] += 1.0; // Church base score
    scores[2] += 1.0; // Pickleball Court base score
    scores[3] += 1.0; // Chinese TikTok base score
    scores[4] += 1.0; // Virginia base score
    scores[5] += 1.0; // Ski Lift base score
    scores[6] += 1.0; // Dating App base score
    scores[7] += 1.0; // Japan base score
    scores[8] += 1.0; // At Home base score
    
    // For debugging
    console.log("Final personality scores:", scores);
    
    // Find personality with highest score
    let highestScore = 0;
    let highestPersonality = 1;
    
    for (const [personality, score] of Object.entries(scores)) {
      if (score > highestScore) {
        highestScore = score;
        highestPersonality = parseInt(personality);
      }
    }
    
    // Set the result
    const foundResult = results.find(r => r.id === highestPersonality);
    setResult(foundResult || results[0]); // Fallback to first result if not found
  };

  // Restart the test
  const handleRestart = () => {
    setStep('intro');
    setCurrentQuestion(1);
    setAnswers({});
    setResult(null);
    setIsProcessingQ28(false);
    setQ28Options([
      "Luca",
      "Christina",
      "Joseph Smith",
      "Pan Dong"
    ]);
  };

  // Calculate progress percentage
  const progress = Math.floor((Object.keys(answers).length / 40) * 100);

  // Render intro slide
  const renderIntroSlide = () => {
      const intro = questions[0];
      
      return (
        <div className="slide fade-in">
          <div className="slide-content">
            <h1>Utah Personality Test</h1>
            <p>{intro.description}</p>
            <button 
              onClick={handleContinue}
              className="primary"
            >
              Continue
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      );
    };

  // Render a question slide
  const renderQuestionSlide = () => {
    const question = questions[currentQuestion];
    
    // For trick question 28, use modified options
    const options = question.id === 28 ? q28Options : question.options;
    
    // Check if this is the last question to conditionally show Submit button
    const isLastQuestion = currentQuestion === 40;
    const hasAnsweredLastQuestion = answers[40] !== undefined;
    
    return (
      <div className="slide fade-in">
        <div className="slide-content">
          {/* Progress bar */}
          <div className="progress-container">
            <div className="progress-info">
              <span>Question {currentQuestion} of 40</span>
              <span>{progress}% Complete</span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
          
          {/* Question */}
          <div className="question-text">
            <h2>{question.text}</h2>
          </div>
          
          {/* Options */}
          <div className="options-container">
            {options && options.map((option, optionIndex) => (
              <button
                key={optionIndex}
                onClick={() => handleAnswer(question.id, optionIndex)}
                className={`option-button ${answers[question.id] === optionIndex ? 'selected' : ''}`}
              >
                <span>{option}</span>
                {answers[question.id] === optionIndex && <Check size={18} />}
              </button>
            ))}
          </div>
          
          {/* Navigation buttons */}
          <div className="navigation-buttons">
            <div>
              {currentQuestion > 1 && (
                <button 
                  onClick={goToPreviousQuestion}
                  className="nav-button"
                >
                  ← Previous question
                </button>
              )}
            </div>
            
            <div>
              {/* Only show next button if answer is selected AND not processing Q28 */}
              {answers[currentQuestion] !== undefined && !isProcessingQ28 && (
                isLastQuestion && hasAnsweredLastQuestion ? (
                  <button 
                    onClick={() => setStep('submit')}
                    className="nav-button"
                  >
                    Continue to results →
                  </button>
                ) : (
                  currentQuestion < 40 && (
                    <button 
                      onClick={goToNextQuestion}
                      className="nav-button"
                    >
                      Next question →
                    </button>
                  )
                )
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

    // Updated renderSubmitSlide function with centered button
    const renderSubmitSlide = () => {
      return (
        <div className="slide fade-in">
          <div className="slide-content">
            <div className="submit-container">
              <h2>You've completed all the questions!</h2>
              <button 
                onClick={handleSubmit}
                className="primary large center"
              >
                Submit Your Answers
              </button>
            </div>
          </div>
        </div>
      );
    };

  // Render processing slide
  const renderProcessingSlide = () => {
    return (
      <div className="slide fade-in">
        <div className="slide-content processing-container">
          <RotateCw size={48} className="processing-icon" />
          <p className="processing-text">Processing your answers...</p>
        </div>
      </div>
    );
  };

  const renderResultsSlide = () => {
      if (!result) return null;
      
      return (
        <div className="slide fade-in">
          <div className="slide-content">
            <h1>Your Ideal Partner</h1>
            <div className="results-container">
              <h2 className="results-title">{result.location}</h2>
              <p className="results-description">{result.description}</p>
              <button 
                onClick={handleRestart}
                className="primary center"
              >
                <RotateCw size={16} />
                Take the Test Again
              </button>
            </div>
          </div>
        </div>
      );
    };


  // Main render based on current step
  return (
    <div className="slide-container">
      {step === 'intro' && renderIntroSlide()}
      {step === 'questions' && renderQuestionSlide()}
      {step === 'submit' && renderSubmitSlide()}
      {step === 'processing' && renderProcessingSlide()}
      {step === 'results' && renderResultsSlide()}
    </div>
  );
};

export default PersonalityTest;
